import React from 'react';
import { createButton } from 'react-simple-wysiwyg';
import { ReactComponent as BoldIcon } from '../../icons/bold.svg';
import { ReactComponent as ItalicIcon } from '../../icons/italic.svg';
import { ReactComponent as LinkIcon } from '../../icons/link-in-bar.svg';
import { ReactComponent as ImageAddIcon } from '../../icons/image-add.svg';
import { ReactComponent as BulletListIcon } from '../../icons/bullet-list.svg';
import { ReactComponent as NumberListIcon } from '../../icons/number-list.svg';
import api from '../../api';
import { createRoot } from 'react-dom/client';
import ThinkingAnimation from '../chat/sidebar/ThinkingAnimation';

export const BtnBold = createButton('Bold', <BoldIcon />, 'bold');
export const BtnItalic = createButton('Italic', <ItalicIcon />, 'italic');
export const BtnLink = createButton('Link', <LinkIcon />, ({ $selection }) => {
  if ($selection?.nodeName === 'A') {
    document.execCommand('unlink');
  } else {
    // eslint-disable-next-line no-alert
    document.execCommand('createLink', false, prompt('URL', '') || undefined);
  }
});
export const BtnBulletList = createButton(
  'Bullet List',
  <BulletListIcon />,
  'insertUnorderedList',
);
export const BtnNumberList = createButton(
  'Number List',
  <NumberListIcon />,
  'insertOrderedList',
);

// BtnImage needs to be created differently since it needs async/await and access to getAccessTokenSilently
// This will be wrapped in the WysiwygEditor component instead
export const createImageButton = (getAccessTokenSilently) => {
  return createButton('Image', <ImageAddIcon />, async () => {
    // File upload only
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg,.gif';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type (only png, jpg, jpeg, gif)
      const allowedTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
      ];
      if (!allowedTypes.includes(file.type)) {
        // eslint-disable-next-line no-alert
        alert('Please select a PNG, JPG, JPEG, or GIF image');
        return;
      }

      // Validate file size (10MB max)
      const MAX_FILE_SIZE = 10 * 1024 * 1024;
      if (file.size > MAX_FILE_SIZE) {
        // eslint-disable-next-line no-alert
        alert('File size exceeds 10MB limit');
        return;
      }

      // Create a container for the ThinkingAnimation
      const placeholderId = `upload-placeholder-${Date.now()}`;
      const container = document.createElement('span');
      container.id = placeholderId;
      container.style.display = 'inline-block';
      container.style.margin = '0 8px';

      // Insert the container into the editor
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.insertNode(container);
        range.collapse(false);
      }

      // Render ThinkingAnimation into the container using React 18 createRoot
      const root = createRoot(container);
      root.render(<ThinkingAnimation showText={false} />);

      // Store reference to the placeholder element
      const placeholderElement = container;

      try {
        const token = await getAccessTokenSilently();
        const fileExtension =
          file.name.split('.').pop()?.toLowerCase() || 'jpg';

        // Get presigned URL using api client
        const { data } = await api.post(
          '/agent-files/presigned-upload',
          { fileExtension },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Upload to S3 (direct to S3, not through api)
        const formData = new FormData();
        Object.entries(data.fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', file);

        const uploadResponse = await fetch(data.url, {
          method: 'POST',
          body: formData,
        });

        if (uploadResponse.ok) {
          // Signature images are embedded in outgoing email, so they need a
          // stable public URL: uploads are private by default, publish it.
          const { data: published } = await api.patch(
            `/agent-files/${data.fileId}/visibility`,
            { visibility: 'public' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          if (!published?.publicUrl) {
            throw new Error('Could not publish signature image');
          }
          // Replace spinner with image
          if (placeholderElement && placeholderElement.parentNode) {
            const img = document.createElement('img');
            img.src = published.publicUrl;
            img.style.maxWidth = '800px';
            img.style.height = 'auto';
            placeholderElement.parentNode.replaceChild(img, placeholderElement);
          }
        } else {
          // Remove spinner on failure
          if (placeholderElement && placeholderElement.parentNode) {
            placeholderElement.parentNode.removeChild(placeholderElement);
          }
          // eslint-disable-next-line no-alert
          alert('Upload failed');
        }
      } catch (error) {
        // Remove spinner on error
        if (placeholderElement && placeholderElement.parentNode) {
          placeholderElement.parentNode.removeChild(placeholderElement);
        }
        // eslint-disable-next-line no-alert
        alert('Upload failed: ' + error.message);
      }
    };
    input.click();
  });
};
